import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import { verifyBusinessGeo, verifySavedBusinessGeo } from "../../../shared/business-geo";
import type { GeoBusinessVerifyResult } from "../../../shared/business-geo";
import {
  mapResourcePointDeleteResult,
  mapResourcePointListPage,
  mapResourcePointListQueryParams,
  mapResourcePointMediaItem,
  mapResourcePointMediaMutationPayload,
  mapResourcePointSkuItem,
  mapResourcePointSkuMutationPayload,
  mapResourcePointSpuItem,
  mapResourcePointSpuMutationPayload,
  type ResourcePointDeleteResultRaw,
  type ResourcePointListPageRaw,
  type ResourcePointMediaItemRaw,
  type ResourcePointSkuItemRaw,
  type ResourcePointSpuItemRaw,
} from "../api/adapters";
import type {
  ResourcePointDeleteResult,
  ResourcePointListPage,
  ResourcePointListParams,
  ResourcePointMediaItem,
  ResourcePointMediaMutation,
  ResourcePointSkuItem,
  ResourcePointSkuMutation,
  ResourcePointSpuItem,
  ResourcePointSpuMutation,
} from "../types";

const deleteRequestConfig = {
  "axios-retry": { retries: 0 },
} as const;

/** 分页查询考察点 SPU。 */
export async function fetchResourcePoints(
  params: ResourcePointListParams,
): Promise<ResourcePointListPage> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(
    "/resource-points",
    { params: mapResourcePointListQueryParams(params) },
  );
  return mapResourcePointListPage(
    requireEnvelopeData(
      response.data,
      "考察点列表接口返回了无效数据",
      isResourcePointListPageRaw,
    ),
  );
}

/** 查询单个考察点 SPU。 */
export async function fetchResourcePoint(spuId: string): Promise<ResourcePointSpuItem> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(
    `/resource-points/${spuId}`,
  );
  return mapResourcePointSpuItem(
    requireEnvelopeData(response.data, "考察点详情接口返回了无效数据", isResourcePointSpuItemRaw),
  );
}

/** 创建考察点 SPU。 */
export async function createResourcePoint(
  payload: ResourcePointSpuMutation,
): Promise<ResourcePointSpuItem> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    "/resource-points",
    mapResourcePointSpuMutationPayload(payload),
    { timeout: 180_000, "axios-retry": { retries: 0 } },
  );
  return mapResourcePointSpuItem(
    requireEnvelopeData(response.data, "考察点创建接口返回了无效数据", isResourcePointSpuItemRaw),
  );
}

/** 更新考察点 SPU。 */
export async function updateResourcePoint(
  spuId: string,
  payload: ResourcePointSpuMutation,
): Promise<ResourcePointSpuItem> {
  const response = await httpClient.put<ApiEnvelope<unknown>>(
    `/resource-points/${spuId}`,
    mapResourcePointSpuMutationPayload(payload),
    { timeout: 180_000, "axios-retry": { retries: 0 } },
  );
  return mapResourcePointSpuItem(
    requireEnvelopeData(response.data, "考察点更新接口返回了无效数据", isResourcePointSpuItemRaw),
  );
}

/** 火车站按当前表单查库匹配电报码；其他已保存考察点沿用快照入口。 */
export function verifyResourcePointGeo(
  spuId: string | undefined,
  payload?: ResourcePointSpuMutation,
): Promise<GeoBusinessVerifyResult> {
  if (payload?.pointType === "火车站") {
    return verifyBusinessGeo({
      province: payload.province ?? "", city: payload.city, district: payload.district,
      areaName: payload.pointName, scenario: "train_station", sourceAddress: payload.address,
      originalLongitude: payload.lng, originalLatitude: payload.lat,
      shortName: payload.pointShortName,
    }, "/resource-points/railway/geo/verify");
  }
  if (!spuId) throw new Error("缺少考察点 ID");
  return verifySavedBusinessGeo(`/resource-points/${spuId}/geo/verify`);
}

/** 调用后端 LLM 润色用户已填简称，仅返回建议文本。 */
export async function polishResourcePointShortName(pointShortName: string): Promise<string> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    "/resource-points/short-name/polish",
    {
      point_short_name: pointShortName,
    },
  );
  const data = requireEnvelopeData(
    response.data,
    "考察点简称润色接口返回了无效数据",
    isResourcePointShortNamePolishRaw,
  );
  return data.point_short_name;
}

/** 逻辑停用考察点 SPU，并禁用自动重试。 */
export async function deleteResourcePoint(spuId: string): Promise<ResourcePointDeleteResult> {
  const response = await httpClient.delete<ApiEnvelope<unknown>>(
    `/resource-points/${spuId}`,
    deleteRequestConfig,
  );
  return mapResourcePointDeleteResult(
    requireEnvelopeData(
      response.data,
      "考察点删除接口返回了无效数据",
      isResourcePointDeleteResultRaw,
    ),
  );
}

/** 查询指定 SPU 下的全部考察点 SKU。 */
export async function fetchResourcePointSkus(spuId: string): Promise<ResourcePointSkuItem[]> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(
    `/resource-points/${spuId}/skus`,
  );
  return requireEnvelopeData(
    response.data,
    "考察点 SKU 列表接口返回了无效数据",
    isResourcePointSkuListRaw,
  ).map(mapResourcePointSkuItem);
}

/** 在指定 SPU 下创建考察点 SKU。 */
export async function createResourcePointSku(
  spuId: string,
  payload: ResourcePointSkuMutation,
): Promise<ResourcePointSkuItem> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    `/resource-points/${spuId}/skus`,
    mapResourcePointSkuMutationPayload(payload),
  );
  return mapResourcePointSkuItem(
    requireEnvelopeData(
      response.data,
      "考察点 SKU 创建接口返回了无效数据",
      isResourcePointSkuItemRaw,
    ),
  );
}

/** 更新指定父级下的考察点 SKU。 */
export async function updateResourcePointSku(
  spuId: string,
  skuId: string,
  payload: ResourcePointSkuMutation,
): Promise<ResourcePointSkuItem> {
  const response = await httpClient.put<ApiEnvelope<unknown>>(
    `/resource-points/${spuId}/skus/${skuId}`,
    mapResourcePointSkuMutationPayload(payload),
  );
  return mapResourcePointSkuItem(
    requireEnvelopeData(
      response.data,
      "考察点 SKU 更新接口返回了无效数据",
      isResourcePointSkuItemRaw,
    ),
  );
}

/** 逻辑停用指定父级下的考察点 SKU，并禁用自动重试。 */
export async function deleteResourcePointSku(
  spuId: string,
  skuId: string,
): Promise<ResourcePointDeleteResult> {
  const response = await httpClient.delete<ApiEnvelope<unknown>>(
    `/resource-points/${spuId}/skus/${skuId}`,
    deleteRequestConfig,
  );
  return mapResourcePointDeleteResult(
    requireEnvelopeData(
      response.data,
      "考察点 SKU 删除接口返回了无效数据",
      isResourcePointDeleteResultRaw,
    ),
  );
}

/** 查询考察点展示媒体列表。 */
export async function fetchResourcePointMedia(spuId: string): Promise<ResourcePointMediaItem[]> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(
    `/resource-points/${spuId}/media`,
  );
  return requireEnvelopeData(
    response.data,
    "考察点媒体列表接口返回了无效数据",
    isResourcePointMediaListRaw,
  ).map(mapResourcePointMediaItem);
}

/** 创建考察点展示媒体元数据。 */
export async function createResourcePointMedia(
  spuId: string,
  payload: ResourcePointMediaMutation,
): Promise<ResourcePointMediaItem> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    `/resource-points/${spuId}/media`,
    mapResourcePointMediaMutationPayload(payload),
    deleteRequestConfig,
  );
  return mapResourcePointMediaItem(
    requireEnvelopeData(
      response.data,
      "考察点媒体创建接口返回了无效数据",
      isResourcePointMediaItemRaw,
    ),
  );
}

/** 更新考察点展示媒体元数据。 */
export async function updateResourcePointMedia(
  spuId: string,
  mediaId: string,
  payload: ResourcePointMediaMutation,
): Promise<ResourcePointMediaItem> {
  const response = await httpClient.put<ApiEnvelope<unknown>>(
    `/resource-points/${spuId}/media/${mediaId}`,
    mapResourcePointMediaMutationPayload(payload),
  );
  return mapResourcePointMediaItem(
    requireEnvelopeData(
      response.data,
      "考察点媒体更新接口返回了无效数据",
      isResourcePointMediaItemRaw,
    ),
  );
}

/** 作废考察点展示媒体。 */
export async function obsoleteResourcePointMedia(
  spuId: string,
  mediaId: string,
): Promise<ResourcePointMediaItem> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    `/resource-points/${spuId}/media/${mediaId}/obsolete`,
    {},
    deleteRequestConfig,
  );
  return mapResourcePointMediaItem(
    requireEnvelopeData(
      response.data,
      "考察点媒体作废接口返回了无效数据",
      isResourcePointMediaItemRaw,
    ),
  );
}

/** 读取统一成功响应中通过业务形状校验的非空数据。 */
function requireEnvelopeData<T>(
  envelope: unknown,
  message: string,
  isData: (value: unknown) => value is T,
): T {
  if (
    !isRecord(envelope)
    || envelope.code !== "OK"
    || !("data" in envelope)
    || envelope.data == null
    || !isData(envelope.data)
  ) {
    throw new Error(message);
  }
  return envelope.data;
}

/** 判断未知数据是否为 SPU 列表页响应。 */
function isResourcePointListPageRaw(value: unknown): value is ResourcePointListPageRaw {
  return isRecord(value)
    && Array.isArray(value.items)
    && value.items.every(isResourcePointSpuItemRaw)
    && isFiniteNumber(value.total)
    && isFiniteNumber(value.page)
    && isFiniteNumber(value.page_size);
}

/** 判断未知数据是否为完整 SPU 响应项。 */
function isResourcePointSpuItemRaw(value: unknown): value is ResourcePointSpuItemRaw {
  if (!isRecord(value)) {
    return false;
  }
  return everyField(value, [
    "resource_point_spu_id",
    "point_type",
    "point_name",
    "country",
    "suitable_major_scope",
    "created_at",
    "updated_at",
    "status",
    "remark",
  ], isString)
    && everyField(value, [
      "legacy_id",
      "point_sub_type",
      "point_short_name",
      "head_image_url",
      "province",
      "city",
      "district",
      "address",
      "visit_mode",
      "suitable_majors",
      "teaching_tags",
    ], isNullableString)
    && isNullableDecimal(value.lng)
    && isNullableDecimal(value.lat)
    && isNullableFiniteNumber(value.default_visit_duration)
    && everyField(value, [
      "itinerary_count",
      "order_itinerary_count",
      "created_by",
      "updated_by",
      "sku_count",
    ], isFiniteNumber)
    && (value.teaching_media_count === undefined || isFiniteNumber(value.teaching_media_count))
    && (value.product_media_count === undefined || isFiniteNumber(value.product_media_count))
    && everyField(value, ["is_ai_created", "is_disabled"], isBoolean);
}

/** 判断未知数据是否为完整 SKU 响应项。 */
function isResourcePointSkuItemRaw(value: unknown): value is ResourcePointSkuItemRaw {
  if (!isRecord(value)) {
    return false;
  }
  return everyField(value, [
    "resource_point_sku_id",
    "resource_point_spu_id",
    "sku_name",
    "ticket_resource_type",
    "traveler_role",
    "settlement_unit",
    "created_at",
    "updated_at",
    "status",
    "remark",
  ], isString)
    && isNullableString(value.reservation_info_markdown)
    && isNullableFiniteNumber(value.min_group_size)
    && isNullableFiniteNumber(value.max_group_size)
    && everyField(value, ["created_by", "updated_by"], isFiniteNumber)
    && everyField(value, ["reservation_required", "is_disabled"], isBoolean);
}

/** 判断未知数据是否为 SKU 响应数组。 */
function isResourcePointSkuListRaw(value: unknown): value is ResourcePointSkuItemRaw[] {
  return Array.isArray(value) && value.every(isResourcePointSkuItemRaw);
}

/** 判断未知数据是否为逻辑删除结果。 */
function isResourcePointDeleteResultRaw(value: unknown): value is ResourcePointDeleteResultRaw {
  return isRecord(value)
    && isString(value.id)
    && isFiniteNumber(value.disabled_sku_count);
}

/** 判断未知数据是否为简称润色结果。 */
function isResourcePointShortNamePolishRaw(
  value: unknown,
): value is { point_short_name: string } {
  return isRecord(value) && isString(value.point_short_name);
}

/** 判断未知数据是否为考察点媒体响应项。 */
function isResourcePointMediaItemRaw(value: unknown): value is ResourcePointMediaItemRaw {
  if (!isRecord(value)) {
    return false;
  }
  return everyField(value, [
    "resource_point_media_id",
    "resource_point_spu_id",
    "media_scene",
    "media_name",
    "file_url",
    "created_at",
    "updated_at",
    "media_status",
    "remark",
  ], isString)
    && isNullableString(value.file_ext)
    && isNullableString(value.content_text)
    && isFiniteNumber(value.sort_no);
}

/** 判断未知数据是否为考察点媒体列表。 */
function isResourcePointMediaListRaw(value: unknown): value is ResourcePointMediaItemRaw[] {
  return Array.isArray(value) && value.every(isResourcePointMediaItemRaw);
}

/** 判断未知值是否为普通对象。 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** 判断一组字段是否都满足同一形状检查。 */
function everyField(
  value: Record<string, unknown>,
  fields: readonly string[],
  predicate: (fieldValue: unknown) => boolean,
): boolean {
  return fields.every((field) => predicate(value[field]));
}

/** 判断未知值是否为字符串。 */
function isString(value: unknown): value is string {
  return typeof value === "string";
}

/** 判断未知值是否为可空字符串。 */
function isNullableString(value: unknown): value is string | null {
  return value === null || isString(value);
}

/** 判断未知值是否为有限数值。 */
function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** 判断未知值是否为可空有限数值。 */
function isNullableFiniteNumber(value: unknown): value is number | null {
  return value === null || isFiniteNumber(value);
}

/** 判断未知值是否为可空 Decimal 数值。 */
function isNullableDecimal(value: unknown): value is number | string | null {
  return value === null
    || isFiniteNumber(value)
    || (isString(value) && value.trim().length > 0 && Number.isFinite(Number(value)));
}

/** 判断未知值是否为布尔值。 */
function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}
